import React, { useState } from 'react'
import { graphql, Link } from 'gatsby'
import styled from 'styled-components'

import SEO from '@components/SEO'
import Layout from '@components/Layout'
import Section from '@components/Section'
import Heading from '@components/Heading'
import LayoutHeroMobile from '@components/Layout/Layout.Hero.Mobile'
import Image from '@components/Image'
import ScrollIndicator from '@components/ScrollIndicator'
import Pill from '@components/Pill'
import Transitions from '@components/Transitions'

import media from '@styles/media'
import { keyToSymbol } from '@shortcuts'

import LabsPreview from '../../sections/labs/Labs.Preview'
import LabsPreviewMobile from '../../sections/labs/Labs.Preview.Mobile'

/**
 * The labs page is a bit of a mess with the inlined Product Array but it
 * works for now. In the event that we redesign this page it's recommended
 * to start a new file to handle things neater!
 */

function LabsPage({ data, location }) {
  const {
    allContentfulPage,
    hero,
    heroBody,
    heroScreen,
    needlBackground,
    memoirBackground,
    feyBackground,
    novelaBackground,
    commandlineBackground,
  } = data
  const { seo } = allContentfulPage.edges[0].node
  const pageBackground =
    'linear-gradient(180deg, #08070B 40%, #111216 50%, #191D23 100%)'
  const navConfig = {
    fixed: true,
    theme: 'light',
    offset: true,
  }
  const footerConfig = {
    visible: true,
  }

  // Start the bulb up animation once the image has laoded
  const [showScreen, setShowScreen] = useState(false)

  // Inlining our products to get the right variables we need in scope!
  const products = [
    {
      logo: NeedlLogo,
      background: needlBackground.childImageSharp.fluid,
      backgroundColor: '#060608',
      excerpt:
        "Whether you're looking to get inked or you're a tattoo artist yourself, this upcoming app will help you get what you need. Find artists and styles, schedule appointments, book flashes and get paid.",
      children: (
        <>
          <HorizontalRule dark />
          <LinkToProduct dark as="div">
            Coming: when it’s ready
          </LinkToProduct>
        </>
      ),
    },
    {
      logo: FeyLogo,
      background: feyBackground.childImageSharp.fluid,
      backgroundColor: '#16171C',
      excerpt:
        'Sick of tracking your trades across Evernote, Excel files and countless screenshots? Fey gives you the complete picture of your portfolio, with fast data entry, always-on risk analysis and more.',
      children: (
        <>
          <HorizontalRule />
          <div>
            <LinkToProduct
              target="_blank"
              data-a11y="false"
              href="https://feyapp.com"
            >
              <GlobeIcon aria-hidden="true" />
              Visit website
            </LinkToProduct>
            <LinkToProduct
              target="_blank"
              data-a11y="false"
              href="https://narative.co/design/open/fey"
            >
              <FigmaIcon aria-hidden="true" /> View in Figma
            </LinkToProduct>
          </div>
        </>
      ),
    },
    {
      logo: CommandlineLogo,
      background: commandlineBackground.childImageSharp.fluid,
      backgroundColor: '#222838',
      excerpt:
        'Computers have plenty of buttons, so we wondered: why explore the web with just one? Narative Command lets you instantly access any page on Narative.co with simple keyboard shortcuts.',
      children: (
        <>
          <HorizontalRule />
          <LinkToProduct dark as="div">
            Try it out! Just press <Symbol>{keyToSymbol('meta')}</Symbol>{' '}
            <Symbol>K</Symbol>
          </LinkToProduct>
        </>
      ),
    },
    {
      logo: NovelaLogo,
      background: novelaBackground.childImageSharp.fluid,
      backgroundColor: '#a8cee8',
      excerpt:
        'With minimal styling and maximum features — including multiple homepage layouts, built-in social sharing and dark mode — Novela makes it easy to publish beautiful articles and stories with Gatsby.',
      children: (
        <>
          <HorizontalRule dark />
          <div>
            <LinkToProduct
              as={Link}
              to="/labs/novela"
              style={{ color: '#08080B' }}
            >
              <EyeIcon aria-hidden="true" fill="#08080B" />
              Preview
            </LinkToProduct>
            <LinkToProduct
              target="_blank"
              data-a11y="false"
              href="https://narative.co/design/open/novela"
              style={{ color: '#08080B' }}
            >
              <FigmaIcon aria-hidden="true" fill="#08080B" /> View in Figma
            </LinkToProduct>
          </div>
        </>
      ),
    },
    {
      logo: MemoirLogo,
      background: memoirBackground.childImageSharp.fluid,
      backgroundColor: '#D4D7DB',
      excerpt:
        'Memoir is a Gatsby theme that puts your writing first. Designed to be beautiful without adding a single image, it includes all the features of our other theme Novela, like dark mode and social sharing.',
      children: (
        <>
          <HorizontalRule dark />
          <LinkToProduct dark as="div">
            Coming soon
          </LinkToProduct>
        </>
      ),
    },
  ].reverse()

  return (
    <Layout nav={navConfig} background={pageBackground} footer={footerConfig}>
      <>
        <SEO
          title={seo.title}
          description={seo.description}
          image={seo.image.file.url}
          pathname={location.pathname}
        />
        <LayoutHeroMobile>
          <HeroSection hideOverflow>
            <ContentContainer>
              <div />
              <Transitions.CSS.FadeIn>
                <TextContainer>
                  <Pill text="Labs" />
                  <Heading.h2 styles="h1">
                    Whether with our clients or all by ourselves, we’re always
                    busy building something new.
                  </Heading.h2>
                  <MainText>
                    Take a peek at the products we’re creating in-house at
                    Narative.
                  </MainText>
                </TextContainer>
              </Transitions.CSS.FadeIn>
              <ScrollIndicator />
            </ContentContainer>

            <HeroImage>
              <Image
                onLoad={() => setShowScreen(true)}
                src={heroBody.childImageSharp.fluid}
              />
              <div
                style={{
                  opacity: showScreen ? 1 : 0,
                  transition: 'opacity 1s ease 0.5s',
                }}
              >
                <Image src={heroScreen.childImageSharp.fluid} />
              </div>
            </HeroImage>
          </HeroSection>
        </LayoutHeroMobile>
        <HeroImageMobile>
          <Image src={hero.childImageSharp.fluid} />
        </HeroImageMobile>
        <LabsBody>
          <Section narrow>
            {products.map(product => (
              <LabsPreview key={product.excerpt} product={product} />
            ))}
          </Section>
          <Section narrow>
            <LabsPreviewMobile />
          </Section>
        </LabsBody>
      </>
    </Layout>
  )
}

export default LabsPage

export const pageQuery = graphql`
  query LabsPageQuery {
    allContentfulPage(filter: { pageName: { eq: "Labs" } }) {
      edges {
        node {
          seo {
            title
            description
            image {
              file {
                url
              }
            }
          }
        }
      }
    }
    hero: file(name: { regex: "/labs-hero-phone/" }) {
      childImageSharp {
        fluid(maxWidth: 1060, quality: 100) {
          ...GatsbyImageSharpFluid_noBase64
        }
      }
    }
    heroBody: file(name: { regex: "/labs-floating-phone-body/" }) {
      childImageSharp {
        fluid(maxWidth: 1060, quality: 100) {
          ...GatsbyImageSharpFluid_noBase64
        }
      }
    }
    heroScreen: file(name: { regex: "/labs-floating-phone-screen/" }) {
      childImageSharp {
        fluid(maxWidth: 1060, quality: 100) {
          ...GatsbyImageSharpFluid_noBase64
        }
      }
    }
    needlBackground: file(name: { regex: "/labs-needl-desktop/" }) {
      childImageSharp {
        fluid(maxWidth: 793, maxHeight: 381, quality: 100) {
          ...GatsbyImageSharpFluid_noBase64
        }
      }
    }
    feyBackground: file(name: { regex: "/labs-fey-desktop/" }) {
      childImageSharp {
        fluid(maxWidth: 793, maxHeight: 381, quality: 100) {
          ...GatsbyImageSharpFluid_noBase64
        }
      }
    }
    commandlineBackground: file(name: { regex: "/labs-command-desktop/" }) {
      childImageSharp {
        fluid(maxWidth: 793, maxHeight: 381, quality: 100) {
          ...GatsbyImageSharpFluid_noBase64
        }
      }
    }
    novelaBackground: file(name: { regex: "/labs-novela-desktop/" }) {
      childImageSharp {
        fluid(maxWidth: 793, maxHeight: 381, quality: 100) {
          ...GatsbyImageSharpFluid_noBase64
        }
      }
    }
    memoirBackground: file(name: { regex: "/labs-memoir-desktop/" }) {
      childImageSharp {
        fluid(maxWidth: 793, maxHeight: 381, quality: 100) {
          ...GatsbyImageSharpFluid_noBase64
        }
      }
    }
  }
`

const HeroSection = styled(Section)`
  display: flex;
  justify-content: space-between;
  align-items: center;

  ${media.tablet`
    pointer-events: none;
  `};
`

const HeroImage = styled.div`
  position: relative;
  width: 610px;
  top: -180px;
  right: -40px;
  animation: float 4.8s ease-in-out infinite 1.4s;

  @keyframes float {
    0% {
      transform: translatey(0px);
    }
    50% {
      transform: translatey(-8px);
    }
    100% {
      transform: translatey(0px);
    }
  }

  ${media.tablet`
    display: none;
  `};

  .gatsby-image-wrapper {
    position: absolute !important;
    top: -180px;
    left: 0;
    width: 100%;
  }
`

const HeroImageMobile = styled(HeroImage)`
  display: none;

  ${media.tablet`
    box-shadow: 0px -40px 40px rgba(8, 7, 11, 0.8);
    display: block;
    width: 100%;
    top: 0;
    right: 0;
    margin-bottom: 60px;
  `};

  .gatsby-image-wrapper {
    position: relative !important;
    top: 0;
    left: 0;
    width: 100%;
  }
`

const TextContainer = styled.div`
  max-width: 560px;

  ${media.phablet`
    position: relative;
    top: -50px;
  `}
`

const MainText = styled.p`
  font-size: 3.2rem;
  font-weight: 400;
  color: ${p => p.theme.colors.grey};
  line-height: 1.3;

  ${media.phablet`
    font-size: 2.2rem;
  `};
`

const ContentContainer = styled.div`
  height: calc(100vh - 140px);
  min-height: 440px;

  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: space-between;

  ${media.tablet`
    height: calc(100vh - 90px);
    padding: 0;
    user-select: none;
    pointer-events: none;
  `};
`

const HorizontalRule = styled.hr`
  width: 140px;
  height: 1px;
  border: none;
  margin-bottom: 30px;
  background: ${p => (p.dark ? 'rgba(0, 0, 0, 0.1)' : 'rgba(255,255,255,0.1)')};

  ${media.phablet`
    width: 100%;
    margin: 0 auto 25px;
    background: ${p =>
      p.dark ? 'rgba(0, 0, 0, 0.1)' : 'rgba(255,255,255,0.1)'};
  `}
`

const LabsBody = styled.div`
  position: relative;
  z-index: 1;
`

const LinkToProduct = styled.a`
  position: relative;
  font-weight: 600;
  font-size: 16px;
  color: ${p => (p.dark ? p.theme.colors.grey : '#fff')};

  svg {
    margin-right: 13px;
  }

  &:nth-child(2) {
    margin-left: 30px;
  }

  &[data-a11y='true']:focus::after {
    content: '';
    position: absolute;
    left: -10%;
    top: -42%;
    width: 120%;
    height: 200%;
    border: 2px solid ${p => p.theme.colors.purple};
    background: rgba(255, 255, 255, 0.01);
    border-radius: 5px;
  }

  ${media.tablet`
    display: block;
    margin: 0 auto;
    color: ${p => (p.dark ? '#000' : '#fff')};

    &:nth-child(2) {
      margin: 15px auto 0;
    }

      svg {
        display: none;
      }
  `}
`

const Symbol = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 16px;
  min-width: 16px;
  text-align: center;
  border-radius: 2.5px;
  padding: 1px 2px;
  color: ${p => p.theme.colors.bg};
  background: ${p => p.theme.colors.moon};
  font-size: 12px;
  font-weight: 400;
  margin-left: 5px;
`

const NeedlLogo = () => (
  <svg
    width="141"
    height="21"
    viewBox="0 0 141 21"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M5.56191 10.7528H7.55691C11.5889 10.7528 12.7439 8.52681 12.7439 6.21681C12.7439 3.17181 10.8329 1.76481 7.51491 1.76481H0.836914V2.77281C1.92891 2.79381 2.26491 3.08781 2.26491 4.28481V13.9028C2.26491 15.0998 1.92891 15.3938 0.836914 15.3938V16.3598H7.34691V15.3938C5.91891 15.3938 5.56191 15.0788 5.56191 13.8818V10.7528ZM9.23691 6.25881C9.23691 8.52681 8.62791 9.19881 6.96891 9.19881H5.56191V3.25581H6.80091C8.62791 3.25581 9.23691 4.05381 9.23691 6.25881Z"
      fill="#FAFAFA"
    />
    <path
      d="M18.39 8.08581C18.348 6.69981 17.886 5.67081 17.739 5.39781H17.424L14.043 6.23781V7.16181C15.093 7.20381 15.303 7.41381 15.303 8.94681V13.9448C15.303 15.1418 15.072 15.3938 14.022 15.4148V16.3598H20.091V15.4148C18.663 15.3938 18.432 15.1418 18.432 13.9448V10.6688C18.432 9.03081 18.852 8.52681 20.448 8.52681C20.952 8.52681 21.225 8.58981 21.603 8.69481L22.191 5.52381C22.023 5.46081 21.729 5.39781 21.414 5.39781C20.259 5.39781 18.915 6.27981 18.39 8.08581Z"
      fill="#FAFAFA"
    />
    <path
      d="M33.4307 10.8578C33.4307 7.18281 31.2467 5.39781 28.2017 5.39781C24.8627 5.39781 22.7627 7.64481 22.7627 11.0468C22.7627 14.5748 24.7367 16.6328 28.0337 16.6328C30.9527 16.6328 33.4307 14.8478 33.4307 10.8578ZM30.0077 10.7738C30.0077 14.0078 29.3777 15.3308 28.1387 15.3308C26.6267 15.3308 26.1857 14.0288 26.1857 11.0888C26.1857 7.77081 26.7737 6.74181 28.0967 6.74181C29.3567 6.74181 30.0077 7.72881 30.0077 10.7738Z"
      fill="#FAFAFA"
    />
    <path
      d="M35.9743 16.0238C35.9743 18.1868 35.5753 19.1738 34.2943 19.7618L34.9033 20.6858C36.8353 20.2238 39.0823 19.1528 39.0823 16.0238V5.50281H38.6203L34.6933 6.23781V7.16181C35.7433 7.20381 35.9743 7.41381 35.9743 8.94681V16.0238ZM39.2713 2.70981C39.2713 1.70181 38.4733 0.882812 37.4443 0.882812C36.3943 0.882812 35.5333 1.70181 35.5333 2.70981C35.5333 3.73881 36.3733 4.55781 37.3813 4.55781C38.4313 4.55781 39.2713 3.73881 39.2713 2.70981Z"
      fill="#FAFAFA"
    />
    <path
      d="M47.0471 14.8058C45.0311 14.8058 44.2331 13.8398 44.1491 11.4668H50.5331V10.5638C50.5331 7.35081 49.1891 5.39781 45.8921 5.39781C42.8471 5.39781 40.7891 7.43481 40.7891 10.9838C40.7891 14.5538 42.7001 16.6118 45.9551 16.6118C47.8241 16.6118 49.2101 16.0448 50.3441 14.9318L49.8401 14.0708C48.9371 14.5538 47.9291 14.8058 47.0471 14.8058ZM45.9131 6.69981C47.0051 6.69981 47.4251 7.49781 47.4251 10.0388H44.1701C44.3171 7.47681 44.8421 6.69981 45.9131 6.69981Z"
      fill="#FAFAFA"
    />
    <path
      d="M60.7453 6.25881C60.0103 5.77581 58.9603 5.41881 57.3013 5.41881C54.3823 5.41881 51.9043 7.41381 51.9043 11.1938C51.9043 14.8688 54.2143 16.6118 57.0283 16.6118C58.6243 16.6118 60.0523 16.0448 61.0183 14.9108L60.4513 14.1128C59.5693 14.6378 58.9603 14.8688 57.9733 14.8688C56.3983 14.8688 55.1383 13.8608 55.1383 10.7108C55.1383 7.49781 56.3143 6.72081 57.3013 6.72081C58.1623 6.72081 58.3303 7.26681 58.4143 8.56881H60.7453V6.25881Z"
      fill="#FAFAFA"
    />
    <path
      d="M68.8113 5.67081H66.2703C66.2703 4.59981 66.3543 3.33981 66.4593 2.58381L64.2753 2.85681C63.7503 3.65481 63.4353 4.68381 63.3093 5.62881L61.9023 6.19581V7.26681H63.1623V13.6088C63.1623 15.9188 64.2333 16.6328 66.1443 16.6328C67.2993 16.6328 68.2023 16.3808 69.0003 15.8768L68.6853 14.7848C68.2023 14.9738 67.8663 15.0368 67.4883 15.0368C66.4383 15.0368 66.2913 14.4698 66.2913 12.9158V7.26681H68.4753L68.8113 5.67081Z"
      fill="#FAFAFA"
    />
    <path
      d="M85.0445 8.35881C85.0445 9.57681 85.1705 11.0048 85.2335 11.4878H85.1285C84.8975 10.8788 84.3935 9.78681 83.7425 8.75781L79.2905 1.76481H74.4395V2.77281C75.5315 2.79381 75.8675 3.08781 75.8675 4.28481V13.9028C75.8675 15.0998 75.5315 15.3938 74.4395 15.3938V16.3598H79.1225V15.3938C78.0095 15.3938 77.6735 15.0998 77.6735 13.9028V8.46381C77.6735 7.11981 77.5475 5.73381 77.4845 5.25081H77.5685C77.7785 5.71281 78.3245 6.84681 79.3115 8.37981L84.4565 16.3598H86.8084V4.28481C86.8084 3.08781 87.1235 2.79381 88.2155 2.77281V1.76481H83.5955V2.77281C84.7085 2.79381 85.0445 3.08781 85.0445 4.28481V8.35881Z"
      fill="#FAFAFA"
    />
    <path
      d="M95.507 14.8058C93.491 14.8058 92.693 13.8398 92.609 11.4668H98.993V10.5638C98.993 7.35081 97.649 5.39781 94.352 5.39781C91.307 5.39781 89.249 7.43481 89.249 10.9838C89.249 14.5538 91.16 16.6118 94.415 16.6118C96.284 16.6118 97.67 16.0448 98.804 14.9318L98.3 14.0708C97.397 14.5538 96.3891 14.8058 95.507 14.8058ZM94.373 6.69981C95.465 6.69981 95.885 7.49781 95.885 10.0388H92.63C92.777 7.47681 93.302 6.69981 94.373 6.69981Z"
      fill="#FAFAFA"
    />
    <path
      d="M106.622 14.8058C104.606 14.8058 103.808 13.8398 103.724 11.4668H110.108V10.5638C110.108 7.35081 108.764 5.39781 105.467 5.39781C102.422 5.39781 100.364 7.43481 100.364 10.9838C100.364 14.5538 102.275 16.6118 105.53 16.6118C107.399 16.6118 108.785 16.0448 109.919 14.9318L109.415 14.0708C108.512 14.5538 107.504 14.8058 106.622 14.8058ZM105.488 6.69981C106.58 6.69981 107 7.49781 107 10.0388H103.745C103.892 7.47681 104.417 6.69981 105.488 6.69981Z"
      fill="#FAFAFA"
    />
    <path
      d="M118.956 14.9318C119.019 15.8138 119.376 16.4648 119.523 16.6328H119.838L123.24 15.8768V15.0158C122.127 14.9318 121.896 14.7638 121.896 13.2308V0.945813H121.35L117.444 1.51281V2.43681C118.473 2.47881 118.788 2.68881 118.788 4.22181V6.44781C118.263 5.79681 117.339 5.41881 116.289 5.41881C113.538 5.41881 111.48 7.14081 111.48 11.0888C111.48 14.9318 113.244 16.6118 115.701 16.6118C117.129 16.6118 118.368 15.9188 118.956 14.9318ZM114.882 10.8578C114.882 7.91781 115.722 7.18281 116.982 7.18281C117.654 7.18281 118.305 7.39281 118.788 7.95981V13.7978C118.2 14.3858 117.549 14.6798 116.856 14.6798C115.596 14.6798 114.882 13.8188 114.882 10.8578Z"
      fill="#FAFAFA"
    />
    <path
      d="M128.433 3.02481C128.433 1.76481 128.16 1.15581 128.055 0.945813H127.74L123.939 1.51281V2.43681C125.01 2.47881 125.325 2.68881 125.325 4.22181V13.9448C125.325 15.1418 125.094 15.3938 124.023 15.4148V16.3598H129.735V15.4148C128.643 15.3938 128.433 15.1208 128.433 13.9238V3.02481Z"
      fill="#FAFAFA"
    />
    <path
      d="M137.117 14.8058C135.101 14.8058 134.303 13.8398 134.219 11.4668H140.603V10.5638C140.603 7.35081 139.259 5.39781 135.962 5.39781C132.917 5.39781 130.859 7.43481 130.859 10.9838C130.859 14.5538 132.77 16.6118 136.025 16.6118C137.894 16.6118 139.28 16.0448 140.414 14.9318L139.91 14.0708C139.007 14.5538 137.999 14.8058 137.117 14.8058ZM135.983 6.69981C137.075 6.69981 137.495 7.49781 137.495 10.0388H134.24C134.387 7.47681 134.912 6.69981 135.983 6.69981Z"
      fill="#FAFAFA"
    />
  </svg>
)

const FeyLogo = () => (
  <svg
    width="69"
    height="26"
    viewBox="0 0 69 26"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <title>Fey Logo</title>
    <path
      d="M59.8015 15.4941L54 6H58.1844L61.5 12.2293L64.8384 6H69L63.2017 15.4941V20.8128C63.2009 21.1274 63.0799 21.4289 62.8653 21.6513C62.6507 21.8737 62.3599 21.9991 62.0564 22H59.8015V15.4941Z"
      fill="white"
    />
    <path
      d="M41.161 6.00002H51V8.06922C51 8.38434 50.8784 8.68656 50.6619 8.90939C50.4454 9.13222 50.1517 9.2574 49.8456 9.2574H43.4338V12.2414H48.6157V14.3106C48.6148 14.6254 48.4929 14.9271 48.2766 15.1497C48.0603 15.3724 47.7672 15.4979 47.4612 15.4987H43.4338V18.746H50.9934V20.8118C50.9939 20.968 50.9643 21.1227 50.9064 21.267C50.8486 21.4114 50.7636 21.5426 50.6563 21.653C50.549 21.7634 50.4216 21.8509 50.2813 21.9105C50.141 21.97 49.9907 22.0004 49.839 22H40V7.1882C40 7.0316 40.0301 6.87654 40.0885 6.73194C40.1469 6.58734 40.2325 6.45606 40.3404 6.34563C40.4483 6.23521 40.5764 6.14783 40.7172 6.08852C40.858 6.02921 41.0088 5.99913 41.161 6.00002Z"
      fill="white"
    />
    <path
      d="M28.2742 22H26V7.18744C26 6.87251 26.1217 6.57048 26.3383 6.34779C26.555 6.1251 26.8488 6 27.1551 6H37V8.06789C36.9991 8.38254 36.8772 8.68406 36.6607 8.90655C36.4443 9.12904 36.151 9.25443 35.8449 9.25532H29.4195V12.2374H34.634V14.3053C34.634 14.6202 34.5122 14.9222 34.2956 15.1449C34.079 15.3676 33.7852 15.4927 33.4788 15.4927H29.4195V20.8159C29.4195 21.1288 29.299 21.429 29.0844 21.6508C28.8698 21.8727 28.5785 21.9982 28.2742 22Z"
      fill="white"
    />
    <path
      d="M12.3557 21.3519C12.5597 21.5565 12.7218 21.8006 12.8324 22.0701C12.943 22.3395 13 22.6289 13 22.9211C13 23.2134 12.943 23.5028 12.8324 23.7722C12.7218 24.0417 12.5597 24.2858 12.3557 24.4904L10.8777 26L9.37507 24.459C8.55033 23.6131 8.06222 22.4836 8.00555 21.29C7.94888 20.0964 8.3277 18.9238 9.06842 18L12.3557 21.3519Z"
      fill="white"
    />
    <path
      d="M12.3612 9.60037C12.7703 10.0148 13 10.5764 13 11.162C13 11.7476 12.7703 12.3093 12.3612 12.7237L8.53364 16.5955C7.98718 17.1448 7.55408 17.7985 7.25948 18.5186C6.96489 19.2386 6.81469 20.0108 6.8176 20.79C6.81853 21.1966 6.86021 21.602 6.942 22L5.43328 20.4319C4.51549 19.5021 4 18.2418 4 16.9279C4 15.6139 4.51549 14.3536 5.43328 13.4238L10.7983 8L12.3612 9.60037Z"
      fill="white"
    />
    <path
      d="M12.3504 1.6047C12.7663 2.02399 13 2.59237 13 3.18499C13 3.77761 12.7663 4.34599 12.3504 4.76528L4.63302 12.5447C3.93442 13.247 3.42076 14.1137 3.13851 15.0662C2.85626 16.0187 2.81432 17.0271 3.01649 18L1.45492 16.4246C0.993711 15.9611 0.627808 15.4106 0.378155 14.8046C0.128502 14.1986 0 13.549 0 12.8929C0 12.2369 0.128502 11.5873 0.378155 10.9813C0.627808 10.3753 0.993711 9.82477 1.45492 9.3613L10.7532 0L12.3504 1.6047Z"
      fill="white"
    />
  </svg>
)

const CommandlineLogo = () => (
  <svg
    width="267"
    height="19"
    viewBox="0 0 267 19"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M12.8163 8.85552C12.8163 10.2475 12.9603 11.8795 13.0323 12.4315H12.9123C12.6483 11.7355 12.0723 10.4875 11.3283 9.31152L6.24029 1.31952H0.696289V2.47152C1.94429 2.49552 2.32829 2.83152 2.32829 4.19952V15.1915C2.32829 16.5595 1.94429 16.8955 0.696289 16.8955V17.9995H6.04829V16.8955C4.77629 16.8955 4.39229 16.5595 4.39229 15.1915V8.97552C4.39229 7.43952 4.24829 5.85552 4.17629 5.30352H4.27229C4.51229 5.83152 5.13629 7.12752 6.26429 8.87952L12.1443 17.9995H14.8323V4.19952C14.8323 2.83152 15.1923 2.49552 16.4403 2.47152V1.31952H11.1603V2.47152C12.4323 2.49552 12.8163 2.83152 12.8163 4.19952V8.85552Z"
      fill="#FAFAFA"
    />
    <path
      d="M28.0347 14.3995C28.0347 12.2155 28.1307 10.2235 28.1307 9.50352C28.1307 6.69552 26.2107 5.47152 23.4507 5.47152C21.5067 5.47152 19.5387 6.07152 18.2667 6.81552V9.26353H20.9067C21.0747 7.99152 21.4587 7.07952 22.8267 7.07952C24.0987 7.07952 24.5547 7.87152 24.5547 9.71952V10.8235C24.2427 10.7995 23.6907 10.7755 23.4027 10.7755C19.6347 10.7755 17.4987 11.9515 17.4987 14.7835C17.4987 17.2555 19.1787 18.3355 21.3147 18.3355C23.2107 18.3355 24.2427 17.3275 24.6507 16.4635C24.8427 17.4715 25.6347 18.1435 27.1227 18.1435C28.1787 18.1435 29.0667 17.8795 29.5707 17.5675V16.5355C28.2987 16.4875 28.0347 16.1995 28.0347 14.3995ZM24.5067 12.1675L24.4347 15.2155C24.0027 15.7675 23.2347 16.1035 22.6107 16.1035C21.6987 16.1035 21.1707 15.6955 21.1707 14.3275C21.1707 12.8635 22.0347 12.1675 23.6667 12.1675H24.5067Z"
      fill="#FAFAFA"
    />
    <path
      d="M35.4757 8.54352C35.4277 6.95952 34.8997 5.78352 34.7317 5.47152H34.3717L30.5077 6.43152V7.48752C31.7077 7.53552 31.9477 7.77552 31.9477 9.52752V15.2395C31.9477 16.6075 31.6837 16.8955 30.4837 16.9195V17.9995H37.4197V16.9195C35.7877 16.8955 35.5237 16.6075 35.5237 15.2395V11.4955C35.5237 9.62352 36.0037 9.04752 37.8277 9.04752C38.4037 9.04752 38.7157 9.11952 39.1477 9.23952L39.8197 5.61552C39.6277 5.54352 39.2917 5.47152 38.9317 5.47152C37.6117 5.47152 36.0757 6.47952 35.4757 8.54352Z"
      fill="#FAFAFA"
    />
    <path
      d="M51.1207 14.3995C51.1207 12.2155 51.2167 10.2235 51.2167 9.50352C51.2167 6.69552 49.2967 5.47152 46.5367 5.47152C44.5927 5.47152 42.6247 6.07152 41.3527 6.81552V9.26353H43.9927C44.1607 7.99152 44.5447 7.07952 45.9127 7.07952C47.1847 7.07952 47.6407 7.87152 47.6407 9.71952V10.8235C47.3287 10.7995 46.7767 10.7755 46.4887 10.7755C42.7207 10.7755 40.5847 11.9515 40.5847 14.7835C40.5847 17.2555 42.2647 18.3355 44.4007 18.3355C46.2967 18.3355 47.3287 17.3275 47.7367 16.4635C47.9287 17.4715 48.7207 18.1435 50.2087 18.1435C51.2647 18.1435 52.1527 17.8795 52.6567 17.5675V16.5355C51.3847 16.4875 51.1207 16.1995 51.1207 14.3995ZM47.5927 12.1675L47.5207 15.2155C47.0887 15.7675 46.3207 16.1035 45.6967 16.1035C44.7847 16.1035 44.2567 15.6955 44.2567 14.3275C44.2567 12.8635 45.1207 12.1675 46.7527 12.1675H47.5927Z"
      fill="#FAFAFA"
    />
    <path
      d="M61.2016 5.78352H58.2976C58.2976 4.55953 58.3936 3.11952 58.5136 2.25552L56.0176 2.56752C55.4176 3.47952 55.0576 4.65552 54.9136 5.73552L53.3056 6.38352V7.60752H54.7456V14.8555C54.7456 17.4955 55.9696 18.3115 58.1536 18.3115C59.4736 18.3115 60.5056 18.0235 61.4176 17.4475L61.0576 16.1995C60.5056 16.4155 60.1216 16.4875 59.6896 16.4875C58.4896 16.4875 58.3216 15.8395 58.3216 14.0635V7.60752H60.8176L61.2016 5.78352Z"
      fill="#FAFAFA"
    />
    <path
      d="M68.6752 17.9995V16.9195C67.4272 16.8955 67.2112 16.6075 67.2112 15.2395V5.59152H66.6832L62.1952 6.43152V7.48752C63.3952 7.53552 63.6352 7.77552 63.6352 9.52752V15.2395C63.6352 16.6075 63.3712 16.8955 62.1712 16.9195V17.9995H68.6752ZM67.4032 2.39952C67.4032 1.24752 66.4912 0.311523 65.3152 0.311523C64.1152 0.311523 63.1552 1.24752 63.1552 2.39952C63.1552 3.57552 64.1152 4.51152 65.2672 4.51152C66.4672 4.51152 67.4032 3.57552 67.4032 2.39952Z"
      fill="#FAFAFA"
    />
    <path
      d="M76.4309 18.1195L79.9589 8.37552C80.3909 7.22352 80.6789 7.00752 81.6389 6.88752V5.78352H76.6229V6.88752C77.6069 6.91152 78.0389 7.15152 78.0389 7.72752C78.0389 7.87152 77.9909 8.06352 77.9189 8.30352L76.3829 13.0315C76.1429 13.8235 75.9269 14.6875 75.9029 14.8795H75.7589C75.7109 14.5195 75.5669 13.8235 75.3029 13.0315L73.6709 8.30352C73.5749 8.06352 73.5269 7.87152 73.5269 7.70352C73.5269 7.19952 73.8869 6.91152 74.8949 6.88752V5.78352H68.3909V6.88752C69.2309 7.00752 69.4709 7.22352 69.9269 8.37552L73.6709 18.1195H76.4309Z"
      fill="#FAFAFA"
    />
    <path
      d="M89.0157 16.2235C86.7117 16.2235 85.7997 15.1195 85.7037 12.4075H92.9997V11.3755C92.9997 7.70352 91.4637 5.47152 87.6957 5.47152C84.2157 5.47152 81.8637 7.79952 81.8637 11.8555C81.8637 15.9355 84.0477 18.2875 87.7677 18.2875C89.9037 18.2875 91.4877 17.6395 92.7837 16.3675L92.2077 15.3835C91.1757 15.9355 90.0237 16.2235 89.0157 16.2235ZM87.7197 6.95952C88.9677 6.95952 89.4477 7.87152 89.4477 10.7755H85.7277C85.8957 7.84752 86.4957 6.95952 87.7197 6.95952Z"
      fill="#FAFAFA"
    />
    <path
      d="M107.661 18.2875C110.061 18.2875 112.125 17.6875 113.109 16.9675V13.7995H110.637C110.325 15.7675 109.701 16.6555 107.805 16.6555C105.741 16.6555 104.301 15.5515 104.301 9.74352C104.301 4.27152 105.549 2.73552 107.805 2.73552C109.461 2.73552 110.013 3.67152 110.397 5.49552H112.989V2.23152C111.837 1.48752 110.205 1.03152 107.853 1.03152C103.245 1.03152 100.101 3.79152 100.101 9.88752C100.101 15.2875 102.765 18.2875 107.661 18.2875Z"
      fill="#FAFAFA"
    />
    <path
      d="M127.032 11.7115C127.032 7.51153 124.536 5.47152 121.056 5.47152C117.24 5.47152 114.84 8.03952 114.84 11.9275C114.84 15.9595 117.096 18.3115 120.864 18.3115C124.2 18.3115 127.032 16.2715 127.032 11.7115ZM123.12 11.6155C123.12 15.3115 122.4 16.8235 120.984 16.8235C119.256 16.8235 118.752 15.3355 118.752 11.9755C118.752 8.18353 119.424 7.00752 120.936 7.00752C122.376 7.00752 123.12 8.13552 123.12 11.6155Z"
      fill="#FAFAFA"
    />
    <path
      d="M140.475 7.60752C139.971 6.28752 138.771 5.49552 137.355 5.49552C135.627 5.49552 134.427 6.31152 133.395 7.55952C133.323 6.59952 132.891 5.75952 132.747 5.47152H132.387L128.523 6.43152V7.48752C129.723 7.53552 129.963 7.77552 129.963 9.52752V15.2395C129.963 16.6075 129.699 16.8955 128.499 16.9195V17.9995H134.883V16.9195C133.659 16.9195 133.539 16.6075 133.539 15.3115V8.87952C134.163 8.44752 134.811 8.01552 135.771 8.01552C136.683 8.01552 137.211 8.51952 137.211 9.95952V15.3115C137.211 16.6075 137.067 16.9195 135.867 16.9195V17.9995H142.083V16.9195C140.955 16.8955 140.787 16.6075 140.787 15.3115V8.80752C141.363 8.39952 142.011 8.01552 142.947 8.01552C143.859 8.01552 144.387 8.51952 144.387 9.95952V15.3115C144.387 16.6075 144.243 16.9195 143.043 16.9195V17.9995H149.379V16.9195C148.203 16.8955 147.939 16.6075 147.939 15.2395V9.11952C147.939 6.64752 146.451 5.49552 144.531 5.49552C142.827 5.49552 141.507 6.33552 140.475 7.60752Z"
      fill="#FAFAFA"
    />
    <path
      d="M162.342 7.60752C161.838 6.28752 160.638 5.49552 159.222 5.49552C157.494 5.49552 156.294 6.31152 155.262 7.55952C155.19 6.59952 154.758 5.75952 154.614 5.47152H154.254L150.39 6.43152V7.48752C151.59 7.53552 151.83 7.77552 151.83 9.52752V15.2395C151.83 16.6075 151.566 16.8955 150.366 16.9195V17.9995H156.75V16.9195C155.526 16.9195 155.406 16.6075 155.406 15.3115V8.87952C156.03 8.44752 156.678 8.01552 157.638 8.01552C158.55 8.01552 159.078 8.51952 159.078 9.95952V15.3115C159.078 16.6075 158.934 16.9195 157.734 16.9195V17.9995H163.95V16.9195C162.822 16.8955 162.654 16.6075 162.654 15.3115V8.80752C163.23 8.39952 163.878 8.01552 164.814 8.01552C165.726 8.01552 166.254 8.51952 166.254 9.95952V15.3115C166.254 16.6075 166.11 16.9195 164.91 16.9195V17.9995H171.246V16.9195C170.07 16.8955 169.806 16.6075 169.806 15.2395V9.11952C169.806 6.64752 168.318 5.49552 166.398 5.49552C164.694 5.49552 163.374 6.33552 162.342 7.60752Z"
      fill="#FAFAFA"
    />
    <path
      d="M182.746 14.3995C182.746 12.2155 182.842 10.2235 182.842 9.50352C182.842 6.69552 180.922 5.47152 178.162 5.47152C176.218 5.47152 174.25 6.07152 172.978 6.81552V9.26353H175.618C175.786 7.99152 176.17 7.07952 177.538 7.07952C178.81 7.07952 179.266 7.87152 179.266 9.71952V10.8235C178.954 10.7995 178.402 10.7755 178.114 10.7755C174.346 10.7755 172.21 11.9515 172.21 14.7835C172.21 17.2555 173.89 18.3355 176.026 18.3355C177.922 18.3355 178.954 17.3275 179.362 16.4635C179.554 17.4715 180.346 18.1435 181.834 18.1435C182.89 18.1435 183.778 17.8795 184.282 17.5675V16.5355C183.01 16.4875 182.746 16.1995 182.746 14.3995ZM179.218 12.1675L179.146 15.2155C178.714 15.7675 177.946 16.1035 177.322 16.1035C176.41 16.1035 175.882 15.6955 175.882 14.3275C175.882 12.8635 176.746 12.1675 178.378 12.1675H179.218Z"
      fill="#FAFAFA"
    />
    <path
      d="M197.795 9.11952C197.795 6.64752 196.283 5.49552 194.315 5.49552C192.443 5.49552 191.171 6.45552 190.139 7.63152C190.067 6.67152 189.611 5.73552 189.443 5.47152H189.083L185.219 6.43152V7.48752C186.419 7.53552 186.659 7.77552 186.659 9.52752V15.2395C186.659 16.6075 186.395 16.8955 185.195 16.9195V17.9995H191.699V16.9195C190.451 16.8955 190.235 16.5835 190.235 15.2875V8.92752C190.859 8.44752 191.579 8.01552 192.635 8.01552C193.643 8.01552 194.219 8.51952 194.219 9.95952V15.2875C194.219 16.5835 194.003 16.8955 192.779 16.9195V17.9995H199.211V16.9195C198.059 16.8955 197.795 16.6075 197.795 15.2395V9.11952Z"
      fill="#FAFAFA"
    />
    <path
      d="M208.955 16.3675C209.027 17.3755 209.435 18.1195 209.603 18.3115H209.963L213.851 17.4475V16.4635C212.579 16.3675 212.315 16.1755 212.315 14.4235V0.383524H211.691L207.227 1.03152V2.08752C208.403 2.13552 208.763 2.37552 208.763 4.12752V6.67152C208.163 5.92752 207.107 5.49552 205.907 5.49552C202.763 5.49552 200.411 7.46352 200.411 11.9755C200.411 16.3675 202.427 18.2875 205.235 18.2875C206.867 18.2875 208.283 17.4955 208.955 16.3675ZM204.299 11.7115C204.299 8.35153 205.259 7.51152 206.699 7.51152C207.467 7.51152 208.211 7.75152 208.763 8.39952V15.0715C208.091 15.7435 207.347 16.0795 206.555 16.0795C205.115 16.0795 204.299 15.0955 204.299 11.7115Z"
      fill="#FAFAFA"
    />
    <path
      d="M222.213 1H219V6.79266H222.213V5.81998H220.129V4.23938H222.126V3.26669H220.129V1.97268H222.213V1Z"
      fill="#E9DAAC"
    />
    <path
      d="M222.718 6.79266H224.073L225.41 4.67361L226.678 6.79266H228.033L226.053 3.73567L227.833 1H226.478L225.41 2.79772L224.394 1H223.039L224.767 3.73567L222.718 6.79266Z"
      fill="#E9DAAC"
    />
    <path
      d="M228.864 1V6.79266H229.993V4.64755H230.722C231.391 4.64755 231.868 4.45649 232.19 4.13516C232.633 3.69224 232.667 3.06695 232.667 2.84115C232.667 2.42428 232.546 1.85978 232.077 1.44292C231.651 1.06948 231.2 1 230.548 1H228.864ZM229.993 1.97268H230.383C230.653 1.97268 230.948 1.99005 231.182 2.15506C231.347 2.27664 231.504 2.49376 231.504 2.81509C231.504 3.06695 231.399 3.32749 231.191 3.4925C230.948 3.67487 230.67 3.67487 230.453 3.67487H229.993V1.97268Z"
      fill="#E9DAAC"
    />
    <path
      d="M236.716 1H233.503V6.79266H236.716V5.81998H234.632V4.23938H236.629V3.26669H234.632V1.97268H236.716V1Z"
      fill="#E9DAAC"
    />
    <path
      d="M237.811 1V6.79266H238.94V4.46518H239.079L240.625 6.79266H242.032L240.225 4.35228C240.503 4.30017 240.712 4.20464 240.859 4.10042C241.311 3.79646 241.537 3.30143 241.537 2.73693C241.537 2.3027 241.407 1.80767 240.964 1.43423C240.694 1.20843 240.286 1 239.487 1H237.811ZM238.94 1.92057H239.279C239.487 1.92057 240.373 1.92926 240.373 2.78904C240.373 3.64013 239.479 3.66619 239.261 3.66619H238.94V1.92057Z"
      fill="#E9DAAC"
    />
    <path d="M242.798 1V6.79266H243.927V1H242.798Z" fill="#E9DAAC" />
    <path
      d="M244.954 6.79266H246.1L246.691 3.22327L248.176 6.79266H248.558L250.112 3.22327L250.633 6.79266H251.78L250.807 1H249.991L248.384 4.68229L246.847 1H246.031L244.954 6.79266Z"
      fill="#E9DAAC"
    />
    <path
      d="M256.019 1H252.806V6.79266H256.019V5.81998H253.935V4.23938H255.932V3.26669H253.935V1.97268H256.019V1Z"
      fill="#E9DAAC"
    />
    <path
      d="M258.243 6.79266V2.91931L261.769 6.79266H262.551V1H261.422V4.86467L257.896 1H257.114V6.79266H258.243Z"
      fill="#E9DAAC"
    />
    <path
      d="M267 1.97268V1H263.266V1.97268H264.568V6.79266H265.697V1.97268H267Z"
      fill="#E9DAAC"
    />
  </svg>
)

const NovelaLogo = () => (
  <svg
    width="198"
    height="24"
    viewBox="0 0 198 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M0 12C0 5.372 5.372 0 12 0C18.628 0 24 5.372 24 12C24 18.628 18.628 24 12 24C5.372 24 0 18.628 0 12ZM2.592 12.12C2.62 14.492 3.522 16.856 5.332 18.666C7.144 20.478 9.508 21.38 11.88 21.408L2.592 12.12ZM2.812 9.914L14.086 21.188C18.288 20.238 21.428 16.49 21.428 12H15.428V13.716H19.518C18.926 16.304 17.034 18.394 14.566 19.268L4.734 9.436C5.794 6.44 8.646 4.286 12 4.286C14.61 4.286 16.916 5.59 18.312 7.58L19.624 6.47C17.91 4.112 15.138 2.572 12 2.572C7.512 2.572 3.762 5.712 2.812 9.914ZM100.986 3.38477C101.106 3.62477 101.418 4.32076 101.418 5.76076V18.2168C101.418 19.5848 101.658 19.8968 102.906 19.9208V21.0008H96.3783V19.9208C97.6023 19.8968 97.8663 19.6088 97.8663 18.2408V7.12877C97.8663 5.37677 97.5063 5.13677 96.2823 5.08877V4.03277L100.626 3.38477H100.986ZM52.8153 11.8568C52.8153 13.2488 52.9593 14.8808 53.0313 15.4328H52.9113C52.6473 14.7368 52.0713 13.4888 51.3273 12.3128L46.2393 4.32077H40.6953V5.47277C41.9433 5.49677 42.3273 5.83277 42.3273 7.20077V18.1928C42.3273 19.5608 41.9433 19.8968 40.6953 19.8968V21.0008H46.0473V19.8968C44.7753 19.8968 44.3913 19.5608 44.3913 18.1928V11.9768C44.3913 10.4408 44.2473 8.85677 44.1753 8.30477H44.2713C44.5113 8.83277 45.1353 10.1288 46.2633 11.8808L52.1433 21.0008H54.8313V7.20077C54.8313 5.83277 55.1913 5.49677 56.4393 5.47277V4.32077H51.1593V5.47277C52.4313 5.49677 52.8153 5.83277 52.8153 7.20077V11.8568ZM69.8126 14.7128C69.8126 10.5128 67.3166 8.47277 63.8366 8.47277C60.0206 8.47277 57.6206 11.0408 57.6206 14.9288C57.6206 18.9608 59.8766 21.3128 63.6446 21.3128C66.9806 21.3128 69.8126 19.2728 69.8126 14.7128ZM65.9006 14.6168C65.9006 18.3128 65.1806 19.8248 63.7646 19.8248C62.0366 19.8248 61.5326 18.3368 61.5326 14.9768C61.5326 11.1848 62.2046 10.0088 63.7166 10.0088C65.1566 10.0088 65.9006 11.1368 65.9006 14.6168ZM82.1064 11.3768L78.5784 21.1208H75.8184L72.0744 11.3768C71.6184 10.2248 71.3784 10.0088 70.5384 9.88877V8.78477H77.0424V9.88877C76.0344 9.91277 75.6744 10.2008 75.6744 10.7048C75.6744 10.8728 75.7224 11.0648 75.8184 11.3048L77.4504 16.0328C77.7144 16.8248 77.8584 17.5208 77.9064 17.8808H78.0504C78.0744 17.6888 78.2904 16.8248 78.5304 16.0328L80.0664 11.3048C80.1384 11.0648 80.1864 10.8728 80.1864 10.7288C80.1864 10.1528 79.7544 9.91277 78.7704 9.88877V8.78477H83.7864V9.88877C82.8264 10.0088 82.5384 10.2248 82.1064 11.3768ZM91.1632 19.2248C88.8592 19.2248 87.9472 18.1208 87.8512 15.4088H95.1472V14.3768C95.1472 10.7048 93.6112 8.47277 89.8432 8.47277C86.3632 8.47277 84.0112 10.8008 84.0112 14.8568C84.0112 18.9368 86.1952 21.2888 89.9152 21.2888C92.0512 21.2888 93.6352 20.6408 94.9312 19.3688L94.3552 18.3848C93.3232 18.9368 92.1712 19.2248 91.1632 19.2248ZM89.8672 9.96077C91.1152 9.96077 91.5952 10.8728 91.5952 13.7768H87.8752C88.0432 10.8488 88.6432 9.96077 89.8672 9.96077ZM114.554 13.7481V13.7479C114.57 13.1992 114.583 12.7674 114.583 12.5048C114.583 9.69677 112.663 8.47277 109.903 8.47277C107.959 8.47277 105.991 9.07277 104.719 9.81677V12.2648H107.359C107.527 10.9928 107.911 10.0808 109.279 10.0808C110.551 10.0808 111.007 10.8728 111.007 12.7208V13.8248C110.695 13.8008 110.143 13.7768 109.855 13.7768C106.087 13.7768 103.951 14.9528 103.951 17.7848C103.951 20.2568 105.631 21.3368 107.767 21.3368C109.663 21.3368 110.695 20.3288 111.103 19.4648C111.295 20.4728 112.087 21.1448 113.575 21.1448C114.631 21.1448 115.519 20.8808 116.023 20.5688V19.5368C114.751 19.4888 114.487 19.2008 114.487 17.4008C114.487 16.0138 114.526 14.7044 114.554 13.7489V13.7485L114.554 13.7481ZM110.959 15.1688L110.887 18.2168C110.455 18.7688 109.687 19.1048 109.063 19.1048C108.151 19.1048 107.623 18.6968 107.623 17.3288C107.623 15.8648 108.487 15.1688 110.119 15.1688H110.959ZM122.086 8.78477L122.35 4.32077H135.646L135.91 8.78477H134.11C133.63 6.72077 133.462 6.02477 131.926 6.02477H130.894V18.1928C130.894 19.5848 131.35 19.8968 132.79 19.8968V21.0008H125.23V19.8968C126.67 19.8968 127.126 19.5848 127.126 18.1928V6.02477H126.094C124.552 6.02477 124.394 6.70515 123.927 8.71545L123.91 8.78477H122.086ZM141.965 5.76076C141.965 4.51276 141.749 3.74477 141.533 3.38477H141.173L136.853 4.03277V5.08877C138.053 5.13677 138.389 5.37677 138.389 7.12877V18.2408C138.389 19.6088 138.149 19.8968 136.949 19.9208V21.0008H143.429V19.9208C142.181 19.8968 141.965 19.5848 141.965 18.2888V11.9288C142.589 11.4488 143.309 11.0168 144.389 11.0168C145.373 11.0168 145.973 11.5208 145.973 12.9608V18.2888C145.973 19.5848 145.757 19.8968 144.509 19.9208V21.0008H150.965V19.9208C149.789 19.8968 149.525 19.6088 149.525 18.2408V12.1208C149.525 9.64877 148.037 8.49677 146.069 8.49677C144.197 8.49677 143.021 9.33677 141.965 10.5128V5.76076ZM156.007 15.4088C156.103 18.1208 157.015 19.2248 159.319 19.2248C160.327 19.2248 161.479 18.9368 162.511 18.3848L163.087 19.3688C161.791 20.6408 160.207 21.2888 158.071 21.2888C154.351 21.2888 152.167 18.9368 152.167 14.8568C152.167 10.8008 154.519 8.47277 157.999 8.47277C161.767 8.47277 163.303 10.7048 163.303 14.3768V15.4088H156.007ZM159.751 13.7768C159.751 10.8728 159.271 9.96077 158.023 9.96077C156.799 9.96077 156.199 10.8488 156.031 13.7768H159.751ZM176.631 10.6088C176.127 9.28877 174.927 8.49677 173.511 8.49677C171.783 8.49677 170.583 9.31277 169.551 10.5608C169.48 9.6262 169.069 8.80535 168.915 8.49681L168.903 8.47277H168.543L164.679 9.43277V10.4888C165.879 10.5368 166.119 10.7768 166.119 12.5288V18.2408C166.119 19.6088 165.855 19.8968 164.655 19.9208V21.0008H171.039V19.9208C169.815 19.9208 169.695 19.6088 169.695 18.3128V11.8808C170.319 11.4488 170.967 11.0168 171.927 11.0168C172.839 11.0168 173.367 11.5208 173.367 12.9608V18.3128C173.367 19.6088 173.223 19.9208 172.023 19.9208V21.0008H178.239V19.9208C177.111 19.8968 176.943 19.6088 176.943 18.3128V11.8088C177.519 11.4008 178.167 11.0168 179.103 11.0168C180.015 11.0168 180.543 11.5208 180.543 12.9608V18.3128C180.543 19.6088 180.399 19.9208 179.199 19.9208V21.0008H185.535V19.9208C184.359 19.8968 184.095 19.6088 184.095 18.2408V12.1208C184.095 9.64877 182.607 8.49677 180.687 8.49677C178.983 8.49677 177.663 9.33677 176.631 10.6088ZM190.578 15.4088C190.674 18.1208 191.586 19.2248 193.89 19.2248C194.898 19.2248 196.05 18.9368 197.082 18.3848L197.658 19.3688C196.362 20.6408 194.778 21.2888 192.642 21.2888C188.922 21.2888 186.738 18.9368 186.738 14.8568C186.738 10.8008 189.09 8.47277 192.57 8.47277C196.338 8.47277 197.874 10.7048 197.874 14.3768V15.4088H190.578ZM194.322 13.7768C194.322 10.8728 193.842 9.96077 192.594 9.96077C191.37 9.96077 190.77 10.8488 190.602 13.7768H194.322Z"
      fill="#08080B"
    />
  </svg>
)

const MemoirLogo = () => (
  <svg
    width="208"
    height="24"
    viewBox="0 0 208 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M0 12C0 5.372 5.372 0 12 0C18.628 0 24 5.372 24 12C24 18.628 18.628 24 12 24C5.372 24 0 18.628 0 12ZM2.592 12.12C2.62 14.492 3.522 16.856 5.332 18.666C7.144 20.478 9.508 21.38 11.88 21.408L2.592 12.12ZM2.812 9.914L14.086 21.188C18.288 20.238 21.428 16.49 21.428 12H15.428V13.716H19.518C18.926 16.304 17.034 18.394 14.566 19.268L4.734 9.436C5.794 6.44 8.646 4.286 12 4.286C14.61 4.286 16.916 5.59 18.312 7.58L19.624 6.47C17.91 4.112 15.138 2.572 12 2.572C7.512 2.572 3.762 5.712 2.812 9.914ZM115.432 19.9205V21.0005H108.928V19.9205C110.128 19.8965 110.392 19.6085 110.392 18.2405V12.5285C110.392 10.7765 110.152 10.5365 108.952 10.4885V9.4325L113.44 8.5925H113.968V18.2405C113.968 19.6085 114.184 19.8965 115.432 19.9205ZM112.072 3.3125C113.248 3.3125 114.16 4.2485 114.16 5.4005C114.16 6.5765 113.224 7.5125 112.024 7.5125C110.872 7.5125 109.912 6.5765 109.912 5.4005C109.912 4.2485 110.872 3.3125 112.072 3.3125ZM58.7756 4.3205H52.5836L50.3036 12.6965C50.0337 13.6797 49.8413 14.8333 49.7262 15.5227L49.7261 15.5234L49.7257 15.5256C49.6978 15.6932 49.6744 15.8332 49.6556 15.9365H49.5596C49.4876 15.3605 49.2716 13.8245 48.9116 12.6965L46.3436 4.3205H40.1276V5.4485C41.8796 5.5205 41.9276 6.0965 41.8556 7.2725L41.1116 18.0245C41.0156 19.4645 40.7756 19.8725 39.4316 19.9205V21.0005H44.6156V19.9205C43.1516 19.9205 43.0076 19.4885 43.0796 18.1685L43.5116 10.7525C43.5701 9.81667 43.6128 8.62743 43.6396 7.87955L43.6396 7.87826V7.87815C43.6458 7.70599 43.6511 7.55728 43.6556 7.4405H43.7756C43.8716 8.0165 44.1596 9.6965 44.5196 10.8485L47.6636 21.0005H49.8956L52.8716 10.2245C53.1356 9.3125 53.3756 7.9205 53.4476 7.4645H53.5676V7.9925C53.5676 8.6165 53.5916 9.7925 53.6396 10.4405L54.2876 18.1925C54.4076 19.4885 54.2396 19.8725 52.6796 19.9205V21.0005H59.6636V19.9205C58.3196 19.8725 58.1276 19.4165 58.0076 18.0725L57.0476 7.2725C56.9276 6.0245 57.0956 5.5445 58.7756 5.4485V4.3205ZM67.851 19.2245C65.547 19.2245 64.635 18.1205 64.539 15.4085H71.835V14.3765C71.835 10.7045 70.299 8.4725 66.531 8.4725C63.051 8.4725 60.699 10.8005 60.699 14.8565C60.699 18.9365 62.883 21.2885 66.603 21.2885C68.739 21.2885 70.323 20.6405 71.619 19.3685L71.043 18.3845C70.011 18.9365 68.859 19.2245 67.851 19.2245ZM66.555 9.9605C67.803 9.9605 68.283 10.8725 68.283 13.7765H64.563C64.731 10.8485 65.331 9.9605 66.555 9.9605ZM82.0421 8.4965C83.4581 8.4965 84.6581 9.2885 85.1621 10.6085C86.1941 9.3365 87.5141 8.4965 89.2181 8.4965C91.1381 8.4965 92.6261 9.6485 92.6261 12.1205V18.2405C92.6261 19.6085 92.8901 19.8965 94.0661 19.9205V21.0005H87.7301V19.9205C88.9301 19.9205 89.0741 19.6085 89.0741 18.3125V12.9605C89.0741 11.5205 88.5461 11.0165 87.6341 11.0165C86.6981 11.0165 86.0501 11.4005 85.4741 11.8085V18.3125C85.4741 19.6085 85.6421 19.8965 86.7701 19.9205V21.0005H80.5541V19.9205C81.7541 19.9205 81.8981 19.6085 81.8981 18.3125V12.9605C81.8981 11.5205 81.3701 11.0165 80.4581 11.0165C79.4981 11.0165 78.8501 11.4485 78.2261 11.8805V18.3125C78.2261 19.6085 78.3461 19.9205 79.5701 19.9205V21.0005H73.1861V19.9205C74.3861 19.8965 74.6501 19.6085 74.6501 18.2405V12.5285C74.6501 10.7765 74.4101 10.5365 73.2101 10.4885V9.4325L77.0741 8.4725H77.4341L77.4462 8.49653C77.6008 8.80506 78.012 9.62592 78.0821 10.5605C79.1141 9.3125 80.3141 8.4965 82.0421 8.4965ZM107.461 14.7125C107.461 10.5125 104.965 8.4725 101.485 8.4725C97.6693 8.4725 95.2693 11.0405 95.2693 14.9285C95.2693 18.9605 97.5253 21.3125 101.293 21.3125C104.629 21.3125 107.461 19.2725 107.461 14.7125ZM103.549 14.6165C103.549 18.3125 102.829 19.8245 101.413 19.8245C99.6853 19.8245 99.1813 18.3365 99.1813 14.9765C99.1813 11.1845 99.8533 10.0085 101.365 10.0085C102.805 10.0085 103.549 11.1365 103.549 14.6165ZM121.491 11.5445C121.443 9.9605 120.915 8.7845 120.747 8.4725H120.387L116.523 9.4325V10.4885C117.723 10.5365 117.963 10.7765 117.963 12.5285V18.2405C117.963 19.6085 117.699 19.8965 116.499 19.9205V21.0005H123.435V19.9205C121.803 19.8965 121.539 19.6085 121.539 18.2405V14.4965C121.539 12.6245 122.019 12.0485 123.843 12.0485C124.419 12.0485 124.731 12.1205 125.163 12.2405L125.835 8.6165C125.643 8.5445 125.307 8.4725 124.947 8.4725C123.627 8.4725 122.091 9.4805 121.491 11.5445ZM131.54 8.7845L131.804 4.3205H145.1L145.364 8.7845H143.564C143.084 6.7205 142.916 6.0245 141.38 6.0245H140.348V18.1925C140.348 19.5845 140.804 19.8965 142.244 19.8965V21.0005H134.684V19.8965C136.124 19.8965 136.58 19.5845 136.58 18.1925V6.0245H135.548C134.005 6.0245 133.847 6.70489 133.38 8.71519L133.364 8.7845H131.54ZM151.418 5.7605C151.418 4.5125 151.202 3.7445 150.986 3.3845H150.626L146.306 4.0325V5.0885C147.506 5.1365 147.842 5.3765 147.842 7.1285V18.2405C147.842 19.6085 147.602 19.8965 146.402 19.9205V21.0005H152.882V19.9205C151.634 19.8965 151.418 19.5845 151.418 18.2885V11.9285C152.042 11.4485 152.762 11.0165 153.842 11.0165C154.826 11.0165 155.426 11.5205 155.426 12.9605V18.2885C155.426 19.5845 155.21 19.8965 153.962 19.9205V21.0005H160.418V19.9205C159.242 19.8965 158.978 19.6085 158.978 18.2405V12.1205C158.978 9.6485 157.49 8.4965 155.522 8.4965C153.65 8.4965 152.474 9.3365 151.418 10.5125V5.7605ZM165.461 15.4085C165.557 18.1205 166.469 19.2245 168.773 19.2245C169.781 19.2245 170.933 18.9365 171.965 18.3845L172.541 19.3685C171.245 20.6405 169.661 21.2885 167.525 21.2885C163.805 21.2885 161.621 18.9365 161.621 14.8565C161.621 10.8005 163.973 8.4725 167.453 8.4725C171.221 8.4725 172.757 10.7045 172.757 14.3765V15.4085H165.461ZM169.205 13.7765C169.205 10.8725 168.725 9.9605 167.477 9.9605C166.253 9.9605 165.653 10.8485 165.485 13.7765H169.205ZM186.084 10.6085C185.58 9.2885 184.38 8.4965 182.964 8.4965C181.236 8.4965 180.036 9.3125 179.004 10.5605C178.934 9.62593 178.523 8.80509 178.368 8.49654L178.356 8.4725H177.996L174.132 9.4325V10.4885C175.332 10.5365 175.572 10.7765 175.572 12.5285V18.2405C175.572 19.6085 175.308 19.8965 174.108 19.9205V21.0005H180.492V19.9205C179.268 19.9205 179.148 19.6085 179.148 18.3125V11.8805C179.772 11.4485 180.42 11.0165 181.38 11.0165C182.292 11.0165 182.82 11.5205 182.82 12.9605V18.3125C182.82 19.6085 182.676 19.9205 181.476 19.9205V21.0005H187.692V19.9205C186.564 19.8965 186.396 19.6085 186.396 18.3125V11.8085C186.972 11.4005 187.62 11.0165 188.556 11.0165C189.468 11.0165 189.996 11.5205 189.996 12.9605V18.3125C189.996 19.6085 189.852 19.9205 188.652 19.9205V21.0005H194.988V19.9205C193.812 19.8965 193.548 19.6085 193.548 18.2405V12.1205C193.548 9.6485 192.06 8.4965 190.14 8.4965C188.436 8.4965 187.116 9.3365 186.084 10.6085ZM200.031 15.4085C200.127 18.1205 201.039 19.2245 203.343 19.2245C204.351 19.2245 205.503 18.9365 206.535 18.3845L207.111 19.3685C205.815 20.6405 204.231 21.2885 202.095 21.2885C198.375 21.2885 196.191 18.9365 196.191 14.8565C196.191 10.8005 198.543 8.4725 202.023 8.4725C205.791 8.4725 207.327 10.7045 207.327 14.3765V15.4085H200.031ZM203.775 13.7765C203.775 10.8725 203.295 9.9605 202.047 9.9605C200.823 9.9605 200.223 10.8485 200.055 13.7765H203.775Z"
      fill="#08080B"
    />
  </svg>
)

const GlobeIcon = ({ fill = 'white' }) => (
  <svg
    width="19"
    height="14"
    viewBox="0 0 19 14"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M9.20896 13.9999C7.34271 14.0075 5.54559 13.2942 4.19251 12.0089C3.99106 11.8171 3.90719 11.5325 3.97251 11.2622C4.03782 10.9919 4.24239 10.7769 4.50915 10.6983C4.77591 10.6197 5.06434 10.6894 5.26579 10.8811C6.33096 11.8879 7.74335 12.445 9.20896 12.4366C9.75609 12.4375 10.3006 12.3616 10.8267 12.2111C11.2297 12.1183 11.6349 12.3572 11.7488 12.7548C11.8627 13.1524 11.6455 13.5697 11.2544 13.7043C10.5898 13.898 9.90125 13.9975 9.20896 13.9999ZM16.3176 8.00353C16.3654 7.67126 16.3888 7.33593 16.3876 7.00024C16.3321 3.08455 13.117 -0.0470396 9.20122 0.000534954C6.45785 -0.00568609 3.94655 1.53903 2.71483 3.99037C2.52583 4.37695 2.68601 4.84355 3.07259 5.03254C3.45918 5.22154 3.92578 5.06136 4.11477 4.67478C5.08411 2.75804 7.05334 1.55361 9.20122 1.5638C12.258 1.51614 14.7767 3.95138 14.8321 7.00802C14.8331 7.26825 14.8149 7.5282 14.7777 7.78576C14.7464 7.99001 14.7978 8.19829 14.9203 8.36463C15.0429 8.53097 15.2266 8.6417 15.431 8.67239H15.5632C15.9535 8.67623 16.2863 8.39008 16.3409 8.00353H16.3176ZM18.5887 9.07689C18.6387 8.88411 18.6545 8.68405 18.6354 8.48581C18.5826 8.06792 18.2068 7.76795 17.7876 7.80917C17.3762 7.86566 17.0817 8.23547 17.1188 8.64913C16.9632 9.23244 13.9456 9.92464 8.81246 8.64913C4.92374 7.66918 2.26385 6.11369 1.65721 5.07929C1.5883 4.98755 1.55264 4.8751 1.5561 4.76041C1.65121 4.35218 1.4057 3.94202 1.00097 3.83297C0.596247 3.72393 0.177909 3.95523 0.0550537 4.35598C-0.0711348 4.8728 0.021739 5.41878 0.311709 5.86481C1.25278 7.45919 4.43376 9.14689 8.42359 10.1502C10.2864 10.6343 12.2001 10.8954 14.1245 10.9279C16.4266 10.9824 18.2387 10.4224 18.5965 9.07689H18.5887Z"
      fill={fill}
    />
  </svg>
)

const EyeIcon = ({ fill = 'white' }) => (
  <svg
    width="20"
    height="12"
    viewBox="0 0 20 12"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M9.80781 11.7636C5.89042 11.6782 2.27087 9.65402 0.147117 6.36116C0.0517275 6.2245 0.000394569 6.06195 0 5.89529C0 5.72095 0.0511518 5.55044 0.147117 5.4049C2.209 2.04657 5.86703 0 9.80781 0C13.7486 0 17.4066 2.04657 19.4685 5.4049C19.6536 5.64369 19.6919 5.96513 19.568 6.24072C19.4442 6.5163 19.1784 6.70107 18.8769 6.72115C18.5754 6.74123 18.2874 6.59336 18.1281 6.33664C16.3064 3.48646 13.1893 1.7258 9.80781 1.63706C6.62406 1.71245 3.66329 3.28829 1.82262 5.88711C3.66329 8.48594 6.62406 10.0618 9.80781 10.1372C12.0105 10.1205 14.1393 9.3406 15.8314 7.93041C16.1827 7.6728 16.6743 7.73593 16.949 8.07393C17.2238 8.41193 17.1852 8.90602 16.8613 9.19725C14.8778 10.8429 12.385 11.7498 9.80781 11.7636ZM12.2596 5.7236C12.2596 4.36942 11.1618 3.27165 9.80766 3.27165C9.35627 3.27165 8.99035 3.63757 8.99035 4.08896C8.99035 4.54036 9.35627 4.90628 9.80766 4.90628C10.2591 4.90628 10.625 5.27221 10.625 5.7236C10.625 6.17499 10.2591 6.54092 9.80766 6.54092C9.35627 6.54092 8.99035 6.17499 8.99035 5.7236C8.99035 5.27221 8.62442 4.90628 8.17303 4.90628C7.72164 4.90628 7.35571 5.27221 7.35571 5.7236C7.35571 7.07778 8.45349 8.17555 9.80766 8.17555C11.1618 8.17555 12.2596 7.07778 12.2596 5.7236Z"
      fill={fill}
    />
  </svg>
)

const FigmaIcon = ({ fill = 'white' }) => (
  <svg
    width="12"
    height="16"
    viewBox="0 0 12 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M1.5172 3.07248C1.5172 2.04865 2.34845 1.2174 3.37228 1.2174H5.22737V4.92756H3.37228C2.34845 4.92756 1.5172 4.09631 1.5172 3.07248ZM3.37228 0C1.6761 0 0.299805 1.3763 0.299805 3.07248C0.299805 4.08075 0.78612 4.97598 1.53686 5.53629C0.78612 6.0966 0.299805 6.99184 0.299805 8.00011C0.299805 9.00831 0.78606 9.9035 1.53672 10.4638C0.78606 11.0241 0.299805 11.9193 0.299805 12.9275C0.299805 14.6237 1.6761 16 3.37228 16C5.06846 16 6.44476 14.6237 6.44476 12.9275V11.0726V10.4639V10.4637V10.4489C6.96038 10.8403 7.60325 11.0726 8.30012 11.0726C9.99631 11.0726 11.3726 9.69629 11.3726 8.00011C11.3726 6.99184 10.8863 6.09661 10.1355 5.5363C10.8863 4.97598 11.3726 4.08075 11.3726 3.07248C11.3726 1.3763 9.99631 0 8.30012 0H6.44476H5.83634H5.83606H5.22764H3.37228ZM3.37228 6.14502C2.34845 6.14502 1.5172 6.97627 1.5172 8.00011C1.5172 9.0177 2.33834 9.84504 3.35357 9.8551L3.37228 9.85504H5.22736V6.14502H3.37228ZM1.5172 12.9275C1.5172 11.9099 2.33834 11.0826 3.35357 11.0725L3.37228 11.0726H5.22736V12.9275C5.22736 13.9514 4.39611 14.7826 3.37228 14.7826C2.34845 14.7826 1.5172 13.9514 1.5172 12.9275ZM6.44504 4.92756H8.30012C9.32396 4.92756 10.1552 4.09631 10.1552 3.07248C10.1552 2.04865 9.32396 1.2174 8.30012 1.2174H6.44504V4.92756ZM6.44504 8.00011C6.44504 6.97627 7.27629 6.14502 8.30012 6.14502C9.32396 6.14502 10.1552 6.97627 10.1552 8.00011C10.1552 9.02394 9.32396 9.85519 8.30012 9.85519C7.27629 9.85519 6.44504 9.02394 6.44504 8.00011Z"
      fill={fill}
    />
  </svg>
)
